import { useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useSearchParams } from 'react-router-dom'
import { useWorkspaceContext } from '~/contexts/WorkspaceContext'
import { useConversationUuid } from '~/hooks/conversation/useConversationUuid'
import { useTaskDetails } from '~/hooks/task/useTaskDetails'
import { useTaskProgressMessages } from '~/hooks/task/useTaskProgressMessages'
import { ModalId, useModalController } from '~/hooks/useModal'
import { AgentTaskStatus } from '~/protos/agents'
import { bridge } from '~/services/bridge'
import { submitRefactorFeedback } from '~/services/taskService'
import { BetterToast } from '~/utils/toast'
import {
  CollapsibleLogCard,
  DownloadIcon,
  EditIcon,
  EmptyState,
  ExpandIcon,
  extractProgressMessagesFromDB
} from '../common'

interface Artifact1LoadingSlideProps {
  taskStatus?: AgentTaskStatus
}

const LOADING_STATES = [
  // The actual loading/processing states for artifact generation
  AgentTaskStatus.REFACTOR_USER_NOTES_ADDED,
  AgentTaskStatus.REFACTOR_PROCESSING_ENDPOINTS,
  AgentTaskStatus.REFACTOR_API_PROPOSING,
  AgentTaskStatus.REFACTOR_API_FEEDBACK_PROVIDED,
  // Include the previous slide's "ready" state to handle the transition period
  // when user navigates here but WebSocket update hasn't arrived yet
  AgentTaskStatus.REFACTOR_EXISTING_DOCS_GENERATED
]

/**
 * Determines if we're in a "refining" state (after feedback) or "generating" state (initial)
 * Logic: We're refining if we've seen REFACTOR_API_FEEDBACK_PROVIDED or if we've seen
 * REFACTOR_API_PROPOSED before (meaning artifacts were generated, then user gave feedback)
 */
function determineLoadingStateType(orderedStateResponses: any[]): 'generating' | 'refining' {
  if (!orderedStateResponses || orderedStateResponses.length === 0) {
    return 'generating'
  }

  // Check if we've ever seen feedback provided or artifacts proposed before
  const hasFeedbackProvided = orderedStateResponses.some(
    tuple => tuple.taskStatus === AgentTaskStatus.REFACTOR_API_FEEDBACK_PROVIDED
  )

  const hasProposedBefore = orderedStateResponses.some(
    tuple => tuple.taskStatus === AgentTaskStatus.REFACTOR_API_PROPOSED
  )

  return hasFeedbackProvided || hasProposedBefore ? 'refining' : 'generating'
}

export function Artifact1LoadingSlide({ taskStatus }: Artifact1LoadingSlideProps) {
  const isPlugin = bridge.isInPlugin()
  const [searchParams] = useSearchParams()
  const conversationUuid = useConversationUuid()
  const { currentWorkspace } = useWorkspaceContext()
  const [feedback, setFeedback] = useState('')
  const [uploadedFile, setUploadedFile] = useState<File | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isImageModalOpen, setIsImageModalOpen] = useState(false)
  const { openModal } = useModalController(ModalId.RefactorFileViewer)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const taskId = searchParams.get('task_id') ?? ''
  const { data: taskDetails } = useTaskDetails(conversationUuid, taskId)

  // Check if we're in a loading state (includes transitioning from previous slide)
  const showLoading = taskStatus ? LOADING_STATES.includes(taskStatus) : false

  // Fetch progress messages from the database - uses WebSocket for real-time updates
  const { data: dbProgressMessages } = useTaskProgressMessages(conversationUuid, taskId, {
    enabled: !!conversationUuid && !!taskId
  })

  // Extract progress messages for the current state using the new DB-backed messages
  const progressMessages = useMemo(
    () => extractProgressMessagesFromDB(dbProgressMessages, taskStatus),
    [dbProgressMessages, taskStatus]
  )

  // Determine if we're generating (initial) or refining (after feedback)
  const loadingStateType = taskDetails?.task?.details?.orderedStateResponses
    ? determineLoadingStateType(taskDetails.task.details.orderedStateResponses)
    : 'generating'

  const isRefining = loadingStateType === 'refining'

  // Extract diffs from task data - check both completed state and streaming states
  // This allows diffs to update incrementally as they stream in
  const { artifactDiffs, depGraphPng } = useMemo(() => {
    if (!taskDetails?.task?.details?.orderedStateResponses) {
      return { artifactDiffs: [], depGraphPng: null }
    }

    const tuples = taskDetails.task.details.orderedStateResponses

    // States to check for diffs (in priority order)
    // First check completed state, then check streaming states
    const statesToCheck = [
      AgentTaskStatus.REFACTOR_API_PROPOSED, // Completed state
      AgentTaskStatus.REFACTOR_API_PROPOSING, // Streaming state
      AgentTaskStatus.REFACTOR_API_FEEDBACK_PROVIDED, // After feedback, regenerating
      AgentTaskStatus.REFACTOR_PROCESSING_ENDPOINTS // Initial processing
    ]

    for (const targetStatus of statesToCheck) {
      // Find the most recent tuple with this status
      for (let i = tuples.length - 1; i >= 0; i--) {
        const tuple = tuples[i]
        if (tuple.taskStatus === targetStatus) {
          const diffs = tuple.response?.[0]?.responseContentStream?.diff || []
          if (diffs.length > 0) {
            // Separate PNG graph from document diffs
            const pngDiff = diffs.find((d: any) => d.bFilePath === 'dep_graph.png')
            const docDiffs = diffs.filter((d: any) => d.bFilePath !== 'dep_graph.png')

            return {
              artifactDiffs: docDiffs,
              depGraphPng: pngDiff?.bFileContent || null
            }
          }
          break
        }
      }
    }

    return { artifactDiffs: [], depGraphPng: null }
  }, [taskDetails])

  const handleViewAllDocs = () => {
    if (artifactDiffs.length > 0) {
      openModal({
        diffs: artifactDiffs
      })
    }
  }

  const handleDownload = () => {
    if (depGraphPng) {
      const link = document.createElement('a')
      link.href = depGraphPng
      link.download = 'dependency_graph.png'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    }
  }

  const handleExpand = () => {
    if (depGraphPng) {
      setIsImageModalOpen(true)
    }
  }

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      setUploadedFile(file)
      // Read file content and set as feedback
      const reader = new FileReader()
      reader.onload = event => {
        const content = event.target?.result as string
        setFeedback(content)
      }
      reader.readAsText(file)
    }
  }

  const handleAttachClick = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    fileInputRef.current?.click()
  }

  const handleRemoveFile = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setUploadedFile(null)
    setFeedback('')
    // Reset file input so the same file can be re-uploaded
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const handleRegenerate = async () => {
    if (!feedback.trim() || !currentWorkspace || !conversationUuid || !taskId) {
      return
    }

    setIsSubmitting(true)
    try {
      await submitRefactorFeedback(
        currentWorkspace.uuid,
        conversationUuid,
        parseInt(taskId),
        feedback
      )
      // Reset feedback and file
      setFeedback('')
      setUploadedFile(null)
    } catch (error) {
      console.error('Failed to submit feedback:', error)
      BetterToast.error('Failed to submit feedback')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className='flex flex-col h-full w-full items-center relative bg-[#fbfbfb] overflow-y-auto'>
      {/* Main Content */}
      <div
        className={`flex flex-col items-center w-full ${showLoading ? '' : 'h-full'} px-[203px] py-[60px]`}
      >
        {showLoading ? (
          /* Loading View - Only show agent progress logs (docs are not streamed midway) */
          <div
            className={`flex flex-col gap-[32px] items-center w-full ${isPlugin ? 'max-w-full pb-6 px-3' : 'max-w-[720px] pb-[60px]'}`}
          >
            <div className='flex flex-col gap-[16px] items-center w-full'>
              <p className="font-['Geist'] font-bold text-[20px] leading-[22px] text-black">
                {isRefining
                  ? 'Refining re-architecture documents'
                  : 'Generating re-architecture documents'}
              </p>
              <p className="font-['Geist'] font-medium text-[14px] leading-[22px] text-[rgba(0,0,0,0.65)] text-center">
                {isRefining
                  ? 'Updating API architecture based on your feedback'
                  : 'Proposing refactored API architecture'}
              </p>
            </div>

            {/* Agent Progress Logs */}
            <CollapsibleLogCard
              messages={progressMessages}
              title='Agent Progress'
              isComplete={taskStatus === AgentTaskStatus.REFACTOR_API_PROPOSED}
            />
          </div>
        ) : depGraphPng ? (
          /* Artifacts View - Large white container with image centered */
          <div className='bg-white border border-solid border-[#eaeaea] rounded-[24px] w-full h-full relative'>
            {/* Action Buttons - Positioned absolutely inside white container */}
            <div className='absolute top-[16px] right-[16px] flex items-center gap-[12px] z-10'>
              {/* Download Icon */}
              <button
                onClick={handleDownload}
                className='flex items-center justify-center h-[36px] px-[16px] py-[8px] rounded-[12px] hover:bg-[rgba(0,0,0,0.04)] transition-colors'
                title='Download graph'
              >
                <DownloadIcon />
              </button>

              {/* Expand Icon */}
              <button
                onClick={handleExpand}
                className='flex items-center justify-center h-[36px] px-[16px] py-[8px] rounded-[12px] hover:bg-[rgba(0,0,0,0.04)] transition-colors'
                title='Expand to full screen'
              >
                <ExpandIcon />
              </button>

              {/* View All Docs Button */}
              <button
                onClick={handleViewAllDocs}
                disabled={artifactDiffs.length === 0}
                className='flex items-center gap-[8px] h-[36px] px-[16px] py-[8px] border border-solid border-[#d6d6d6] rounded-[12px] hover:bg-[rgba(0,0,0,0.02)] transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
              >
                <EditIcon />
                <span className="font-['Geist'] font-medium text-[14px] leading-[22px] text-[rgba(38,38,38,1)]">
                  View All Docs
                </span>
              </button>
            </div>

            {/* Centered Image Container */}
            <div className='absolute inset-0 flex items-center justify-center p-[60px]'>
              <img
                src={depGraphPng}
                alt='Dependency Graph'
                className='max-w-full max-h-full object-contain'
              />
            </div>
          </div>
        ) : (
          /* No artifacts yet */
          <EmptyState message='No artifacts available' className='w-full h-full' />
        )}
      </div>

      {/* Bottom Feedback Box - Only show when artifacts are ready */}
      {!showLoading && depGraphPng && (
        <div className='absolute bottom-0 left-0 right-0 flex flex-col gap-[10px] items-center justify-center pb-[48px] pt-[24px] px-[10px]'>
          <div
            tabIndex={0}
            className={`border border-solid border-[rgba(91,91,213,0.2)] focus:border-[#846fe1] focus-within:border-[#846fe1] flex gap-[10px] items-center max-w-[800px] min-h-[64px] px-[16px] py-[16px] rounded-[24px] w-full transition-colors shadow-[0px_2px_4px_0px_rgba(0,0,0,0.02),0px_1px_6px_-1px_rgba(0,0,0,0.02),0px_1px_2px_0px_rgba(0,0,0,0.03)] outline-none ${
              uploadedFile ? 'bg-[#f5f5f5]' : 'bg-white'
            }`}
          >
            {/* Hidden file input - only plain text files */}
            <input
              ref={fileInputRef}
              type='file'
              accept='.txt,.md,.xml'
              onChange={handleFileUpload}
              className='hidden'
            />

            {/* Content area - grows to fill available space */}
            <div className='flex-1 flex items-center gap-[2px] min-w-0'>
              {uploadedFile ? (
                /* File uploaded state - show file icon, filename, and delete button on hover */
                <div className='group/file flex items-center gap-[8px] overflow-clip px-[16px] py-[4px] rounded-[16px] hover:bg-[rgba(0,0,0,0.04)] transition-colors cursor-pointer'>
                  <div className='flex items-center gap-[16px]'>
                    {/* File Icon */}
                    <div className='flex items-center justify-center size-[24px]'>
                      <svg
                        width='18'
                        height='18'
                        viewBox='0 0 18 18'
                        fill='none'
                        xmlns='http://www.w3.org/2000/svg'
                      >
                        <path
                          d='M10.5 1.5H4.5C4.10218 1.5 3.72064 1.65804 3.43934 1.93934C3.15804 2.22064 3 2.60218 3 3V15C3 15.3978 3.15804 15.7794 3.43934 16.0607C3.72064 16.342 4.10218 16.5 4.5 16.5H13.5C13.8978 16.5 14.2794 16.342 14.5607 16.0607C14.842 15.7794 15 15.3978 15 15V6L10.5 1.5Z'
                          stroke='#000000'
                          strokeWidth='1.5'
                          strokeLinecap='round'
                          strokeLinejoin='round'
                        />
                        <path
                          d='M10.5 1.5V6H15'
                          stroke='#000000'
                          strokeWidth='1.5'
                          strokeLinecap='round'
                          strokeLinejoin='round'
                        />
                        <path
                          d='M6 9.75H12'
                          stroke='#000000'
                          strokeWidth='1.5'
                          strokeLinecap='round'
                          strokeLinejoin='round'
                        />
                        <path
                          d='M6 12.75H12'
                          stroke='#000000'
                          strokeWidth='1.5'
                          strokeLinecap='round'
                          strokeLinejoin='round'
                        />
                      </svg>
                    </div>
                    {/* Filename */}
                    <span className="font-['Geist'] font-medium text-[14px] leading-[20px] text-[rgba(0,0,0,0.88)] text-center whitespace-nowrap tracking-[0.1px]">
                      {uploadedFile.name}
                    </span>
                  </div>
                  {/* Delete Button - Only visible on hover */}
                  <button
                    type='button'
                    onClick={handleRemoveFile}
                    className='flex items-center justify-center size-[24px] hover:bg-[rgba(0,0,0,0.08)] rounded-[4px] transition-colors opacity-0 group-hover/file:opacity-100'
                    title='Remove file'
                  >
                    <svg
                      width='18'
                      height='18'
                      viewBox='0 0 18 18'
                      fill='none'
                      xmlns='http://www.w3.org/2000/svg'
                    >
                      <path
                        d='M2.25 4.5H15.75'
                        stroke='#000000'
                        strokeWidth='1.5'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                      />
                      <path
                        d='M14.25 4.5V15C14.25 15.3978 14.092 15.7794 13.8107 16.0607C13.5294 16.342 13.1478 16.5 12.75 16.5H5.25C4.85218 16.5 4.47064 16.342 4.18934 16.0607C3.90804 15.7794 3.75 15.3978 3.75 15V4.5'
                        stroke='#000000'
                        strokeWidth='1.5'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                      />
                      <path
                        d='M6 4.5V3C6 2.60218 6.15804 2.22064 6.43934 1.93934C6.72064 1.65804 7.10218 1.5 7.5 1.5H10.5C10.8978 1.5 11.2794 1.65804 11.5607 1.93934C11.842 2.22064 12 2.60218 12 3V4.5'
                        stroke='#000000'
                        strokeWidth='1.5'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                      />
                    </svg>
                  </button>
                </div>
              ) : (
                /* No file - show attach button and input field */
                <>
                  {/* Attach Icon Button */}
                  <button
                    type='button'
                    onClick={handleAttachClick}
                    className='flex items-center justify-center shrink-0 size-[32px] rounded-[12px] hover:bg-[rgba(0,0,0,0.04)] transition-colors'
                    title='Attach file'
                  >
                    <svg
                      width='16'
                      height='16'
                      viewBox='0 0 16 16'
                      fill='none'
                      xmlns='http://www.w3.org/2000/svg'
                    >
                      <path
                        d='M14.2929 7.77472L8.16618 13.9014C7.41565 14.652 6.39766 15.0737 5.33621 15.0737C4.27475 15.0737 3.25677 14.652 2.50621 13.9014C1.75564 13.1509 1.33398 12.1329 1.33398 11.0714C1.33398 10.0099 1.75564 8.99199 2.50621 8.24139L8.63285 2.11473C9.13325 1.61436 9.81192 1.33325 10.5195 1.33325C11.2272 1.33325 11.9059 1.61436 12.4062 2.11473C12.9066 2.61511 13.1877 3.29377 13.1877 4.0014C13.1877 4.70904 12.9066 5.38769 12.4062 5.88807L6.27288 12.0147C6.02269 12.2649 5.68336 12.4055 5.32954 12.4055C4.97572 12.4055 4.6364 12.2649 4.38621 12.0147C4.13602 11.7645 3.99546 11.4253 3.99546 11.0714C3.99546 10.7176 4.13602 10.3783 4.38621 10.1281L10.0462 4.47473'
                        stroke='rgba(0,0,0,0.25)'
                        strokeWidth='1.5'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                      />
                    </svg>
                  </button>
                  {/* Text Input */}
                  <input
                    type='text'
                    value={feedback}
                    onChange={e => setFeedback(e.target.value)}
                    placeholder='Provide feedback on the refactored documentation and regenerate...'
                    className="flex-1 font-['Geist'] font-medium text-[14px] leading-[22px] bg-transparent border-none outline-none text-[rgba(0,0,0,0.88)] placeholder:text-[rgba(0,0,0,0.25)]"
                  />
                </>
              )}
            </div>

            {/* Control Bar - Regenerate Button */}
            <div className='flex items-end justify-end h-[32px] shrink-0'>
              <button
                disabled={!feedback.trim() || isSubmitting}
                onClick={handleRegenerate}
                className={`flex items-center justify-center rounded-[12px] px-[12px] py-[8px] transition-colors bg-[rgba(132,111,225,0.1)] text-[#846fe1] ${
                  !feedback.trim() || isSubmitting
                    ? 'cursor-not-allowed'
                    : 'hover:bg-[rgba(132,111,225,0.15)] cursor-pointer'
                }`}
              >
                <span className="font-['Geist'] font-medium text-[14px] leading-[22px]">
                  {isSubmitting ? 'Submitting...' : 'Regenerate'}
                </span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Image Popup Modal - Rendered via Portal */}
      {isImageModalOpen &&
        depGraphPng &&
        createPortal(
          <div
            className='fixed inset-0 z-[9999] bg-black/50 flex items-center justify-center p-[16px]'
            onClick={() => setIsImageModalOpen(false)}
          >
            {/* Popup Container */}
            <div
              className='relative bg-white rounded-[16px] shadow-2xl w-[calc(100vw-32px)] h-[calc(100vh-32px)] overflow-hidden'
              onClick={e => e.stopPropagation()}
            >
              {/* Close Button */}
              <button
                onClick={() => setIsImageModalOpen(false)}
                className='absolute top-[16px] right-[16px] flex items-center justify-center size-[32px] rounded-full bg-white border border-solid border-[rgba(0,0,0,0.1)] hover:bg-[rgba(0,0,0,0.04)] transition-colors z-10 shadow-md'
                title='Close'
              >
                <svg
                  width='16'
                  height='16'
                  viewBox='0 0 16 16'
                  fill='none'
                  xmlns='http://www.w3.org/2000/svg'
                >
                  <path
                    d='M12 4L4 12M4 4L12 12'
                    stroke='rgba(0,0,0,0.88)'
                    strokeWidth='1.5'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                </svg>
              </button>

              {/* Image Container */}
              <div className='flex items-center justify-center h-full w-full p-[24px]'>
                <img
                  src={depGraphPng}
                  alt='Dependency Graph'
                  className='max-w-full max-h-full object-contain'
                />
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  )
}
